import { apiFetch } from '@/lib/api';
import type {
  BacenAppeals,
  BacenCaseDetail,
  BacenCaseList,
  BacenCollection,
  BacenConsistency,
  BacenEntities,
  BacenEntity,
  BacenOverview,
  BacenPenalties,
  BacenRegisters,
  BacenSettlements,
  BacenTimeline,
  CrsfnAcordao,
  CrsfnBoard,
  CrsfnDispositions,
  CrsfnRelator,
  CrsfnRelators,
  CrsfnSearch,
  CrsfnVotes,
} from '@/services/types';

/**
 * The sixth agency. Same envelope, same substrate contract. Two families of
 * screens: the first-instance ledger (one row per accused in one PAS) and the
 * CRSFN collegiate slice over the acordaos linked to a ledger row.
 */
export function fetchBacenOverview(): Promise<BacenOverview> {
  return apiFetch<BacenOverview>('/api/v1/bacen/overview');
}

export interface BacenCaseQuery {
  query?: string | null;
  penalty_code?: string | null;
  status_family?: string | null;
  office?: string | null;
  year?: number | null;
  /** `person` or `company`. */
  subject_type?: string | null;
  appeal_kind?: string | null;
  trajectory?: string | null;
  /** The company's full 14-digit CNPJ; anything shorter matches nothing. */
  entity?: string | null;
  in_registry?: boolean | null;
  limit?: number;
  offset?: number;
}

/**
 * The filter params, without the page bounds, so the CSV export replays the
 * SCREEN's exact query (`limit`/`offset` describe a page, never a result).
 */
export function bacenCaseParams(query: BacenCaseQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.penalty_code) params.set('penalty_code', query.penalty_code);
  if (query.status_family) params.set('status_family', query.status_family);
  if (query.office) params.set('office', query.office);
  if (query.year) params.set('year', String(query.year));
  if (query.subject_type) params.set('subject_type', query.subject_type);
  if (query.appeal_kind) params.set('appeal_kind', query.appeal_kind);
  if (query.trajectory) params.set('trajectory', query.trajectory);
  if (query.entity) params.set('entity', query.entity);
  if (query.in_registry !== null && query.in_registry !== undefined) {
    params.set('in_registry', String(query.in_registry));
  }
  return params;
}

export function fetchBacenCases(query: BacenCaseQuery): Promise<BacenCaseList> {
  const params = bacenCaseParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<BacenCaseList>(`/api/v1/bacen/cases?${params.toString()}`);
}

/** One PAS: every accused it names, each with its own walk. */
export function fetchBacenCase(key: string): Promise<BacenCaseDetail> {
  return apiFetch<BacenCaseDetail>(`/api/v1/bacen/c/${encodeURIComponent(key)}`);
}

export interface BacenEntityQuery {
  query?: string | null;
  in_registry?: boolean | null;
  limit?: number;
  offset?: number;
}

export function bacenEntityParams(query: BacenEntityQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.in_registry !== null && query.in_registry !== undefined) {
    params.set('in_registry', String(query.in_registry));
  }
  return params;
}

export function fetchBacenEntities(query: BacenEntityQuery): Promise<BacenEntities> {
  const params = bacenEntityParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<BacenEntities>(`/api/v1/bacen/entities?${params.toString()}`);
}

/** One company, by its full 14-digit CNPJ. Persons have no profile, by decision. */
export function fetchBacenEntity(cnpj: string): Promise<BacenEntity> {
  return apiFetch<BacenEntity>(`/api/v1/bacen/e/${encodeURIComponent(cnpj)}`);
}

// --------------------------------------------------------------------------- //
// The ledger screens
// --------------------------------------------------------------------------- //

export function fetchBacenAppeals(): Promise<BacenAppeals> {
  return apiFetch<BacenAppeals>('/api/v1/bacen/appeals');
}

export function fetchBacenPenalties(): Promise<BacenPenalties> {
  return apiFetch<BacenPenalties>('/api/v1/bacen/penalties');
}

export function fetchBacenCollection(): Promise<BacenCollection> {
  return apiFetch<BacenCollection>('/api/v1/bacen/collection');
}

export interface BacenConsistencyQuery {
  /** SAME_PAS, OFFICE_TYPE_YEAR or PROVISION. */
  basis?: string | null;
  key?: string | null;
  limit?: number;
  offset?: number;
}

export function bacenConsistencyParams(query: BacenConsistencyQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.basis) params.set('basis', query.basis);
  if (query.key) params.set('key', query.key);
  return params;
}

export function fetchBacenConsistency(query: BacenConsistencyQuery): Promise<BacenConsistency> {
  const params = bacenConsistencyParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<BacenConsistency>(`/api/v1/bacen/consistency?${params.toString()}`);
}

export function fetchBacenTimeline(): Promise<BacenTimeline> {
  return apiFetch<BacenTimeline>('/api/v1/bacen/timeline');
}

export interface BacenRegisterQuery {
  /** QGI (disqualified) or QGP (barred from auditing). */
  register?: 'QGI' | 'QGP';
  limit?: number;
  offset?: number;
}

export function bacenRegisterParams(query: BacenRegisterQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set('register', query.register ?? 'QGI');
  return params;
}

export function fetchBacenRegisters(query: BacenRegisterQuery): Promise<BacenRegisters> {
  const params = bacenRegisterParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<BacenRegisters>(`/api/v1/bacen/registers?${params.toString()}`);
}

export interface BacenPageQuery {
  limit?: number;
  offset?: number;
}

export function fetchBacenSettlements(query: BacenPageQuery = {}): Promise<BacenSettlements> {
  const params = new URLSearchParams();
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<BacenSettlements>(`/api/v1/bacen/settlements?${params.toString()}`);
}

// --------------------------------------------------------------------------- //
// The CRSFN slice: acordaos linked to a BACEN ledger row
// --------------------------------------------------------------------------- //

export function fetchCrsfnBoard(query: BacenPageQuery = {}): Promise<CrsfnBoard> {
  const params = new URLSearchParams();
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CrsfnBoard>(`/api/v1/bacen/crsfn/board?${params.toString()}`);
}

export function fetchCrsfnRelators(): Promise<CrsfnRelators> {
  return apiFetch<CrsfnRelators>('/api/v1/bacen/crsfn/relators');
}

/** One relator, by the slug of the canonical name. */
export function fetchCrsfnRelator(slug: string): Promise<CrsfnRelator> {
  return apiFetch<CrsfnRelator>(`/api/v1/bacen/crsfn/r/${encodeURIComponent(slug)}`);
}

export function fetchCrsfnVotes(): Promise<CrsfnVotes> {
  return apiFetch<CrsfnVotes>('/api/v1/bacen/crsfn/votes');
}

export function fetchCrsfnDispositions(): Promise<CrsfnDispositions> {
  return apiFetch<CrsfnDispositions>('/api/v1/bacen/crsfn/dispositions');
}

export interface CrsfnSearchQuery {
  q?: string | null;
  limit?: number;
}

export function crsfnSearchParams(query: CrsfnSearchQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  return params;
}

export function fetchCrsfnSearch(query: CrsfnSearchQuery): Promise<CrsfnSearch> {
  const params = crsfnSearchParams(query);
  params.set('limit', String(query.limit ?? 50));
  return apiFetch<CrsfnSearch>(`/api/v1/bacen/crsfn/search?${params.toString()}`);
}

/** One acordao, by its key: `2024-0172` (annual series) or `g2014-11296` (global). */
export function fetchCrsfnAcordao(key: string): Promise<CrsfnAcordao> {
  return apiFetch<CrsfnAcordao>(`/api/v1/bacen/crsfn/a/${encodeURIComponent(key)}`);
}
